import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ConnectionTester = ({ integrations, onTestConnection }) => {
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [testResults, setTestResults] = useState({});
  const [isRunningTest, setIsRunningTest] = useState(false);

  const connectedIntegrations = integrations?.filter(int => int?.status === 'connected');

  const integrationOptions = connectedIntegrations?.map(integration => ({
    value: integration?.id,
    label: integration?.name,
    description: integration?.category
  }));

  const handleTestConnection = async () => {
    if (!selectedIntegration) return;
    
    setIsRunningTest(true);
    
    try {
      // Simulate test process
      const steps = [
        { name: 'Authentication', duration: 1000 },
        { name: 'Network Connectivity', duration: 800 },
        { name: 'API Endpoints', duration: 1200 },
        { name: 'Data Validation', duration: 900 }
      ];

      const results = {
        integrationId: selectedIntegration,
        timestamp: new Date()?.toISOString(),
        steps: [],
        overall: 'success'
      };

      for (const step of steps) {
        await new Promise(resolve => setTimeout(resolve, step.duration));
        
        // Simulate random success/failure (90% success rate)
        const success = Math.random() > 0.1;
        
        results?.steps?.push({
          name: step?.name,
          status: success ? 'success' : 'error',
          duration: step?.duration,
          message: success ? 'Test passed successfully' : 'Connection timeout or authentication failed'
        });

        if (!success) {
          results.overall = 'error';
        }
      }

      setTestResults(prev => ({
        ...prev,
        [selectedIntegration]: results
      }));

      if (onTestConnection) {
        onTestConnection(selectedIntegration, results);
      }
    } catch (error) {
      console.error('Test failed:', error);
    } finally {
      setIsRunningTest(false);
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'success':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      case 'running':
        return 'Loader';
      default:
        return 'Circle';
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-success';
      case 'error':
        return 'text-destructive';
      case 'running':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const currentResults = selectedIntegration ? testResults?.[selectedIntegration] : null;

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">Connection Tester</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Test and validate your integration connections
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Zap" size={20} className="text-accent" />
          <span className="text-sm font-medium text-accent">Real-time</span>
        </div>
      </div>
      {/* Test Configuration */}
      <div className="space-y-4 mb-6">
        <Select
          label="Select Integration"
          placeholder="Choose an integration to test"
          options={integrationOptions}
          value={selectedIntegration}
          onChange={setSelectedIntegration}
          disabled={isRunningTest}
        />

        <Button
          variant="default"
          onClick={handleTestConnection}
          disabled={!selectedIntegration || isRunningTest}
          loading={isRunningTest}
          iconName="Play"
          iconPosition="left"
          fullWidth
        >
          {isRunningTest ? 'Running Tests...' : 'Run Connection Test'}
        </Button>
      </div>
      {/* Test Results */}
      {currentResults && (
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-card-foreground">Test Results</h3>
            <div className="flex items-center space-x-2">
              <Icon 
                name={currentResults?.overall === 'success' ? 'CheckCircle' : 'XCircle'} 
                size={16} 
                className={currentResults?.overall === 'success' ? 'text-success' : 'text-destructive'}
              />
              <span className={`text-sm font-medium capitalize ${
                currentResults?.overall === 'success' ? 'text-success' : 'text-destructive'
              }`}>
                {currentResults?.overall}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {currentResults?.steps?.map((step, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={getStepIcon(step?.status)} 
                    size={16} 
                    className={`${getStepColor(step?.status)} ${
                      step?.status === 'running' ? 'animate-spin' : ''
                    }`}
                  />
                  <div>
                    <p className="font-medium text-card-foreground">{step?.name}</p>
                    <p className="text-xs text-muted-foreground">{step?.message}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-card-foreground">{step?.duration}ms</p>
                  <p className="text-xs text-muted-foreground">Response time</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Test completed:</span>
              <span className="font-medium text-card-foreground">
                {new Date(currentResults.timestamp)?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Quick Test Options */}
      {connectedIntegrations?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="font-medium text-card-foreground mb-3">Quick Tests</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {connectedIntegrations?.slice(0, 4)?.map((integration) => (
              <Button
                key={integration?.id}
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedIntegration(integration?.id);
                  setTimeout(handleTestConnection, 100);
                }}
                disabled={isRunningTest}
                className="justify-start"
              >
                <Icon name={integration?.icon} size={14} className="mr-2" />
                {integration?.name}
              </Button>
            ))}
          </div>
        </div>
      )}
      {connectedIntegrations?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Unplug" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="font-medium text-card-foreground mb-2">No Connected Integrations</h3>
          <p className="text-sm text-muted-foreground">
            Connect at least one integration to run connection tests
          </p>
        </div>
      )}
    </div>
  );
};

export default ConnectionTester;